import * as THREE from 'three';
import { G, mesh } from '../render/scene';
import { MAT_P, coreGlow } from '../render/materials';
import { bevel, joint, mark, plate, ram, rib, seam } from './mech-kit';
import { TPOSE } from './titan-pose';
/* ============================================================
   TITAN-3 · 主武裝 MAIN（右臂榴彈砲）與掛架 PYLON（左肩火箭巢）
   ============================================================

   CHASSIS-0 的手臂是「拿槍的架子」（細、直），LANCER-2 的手臂本身是武器
   （整條有肉）。這一台是第三種：**手臂是砲架** —— 上臂粗到與軀幹同一個
   量級，前臂短而方，砲身直接坐在前臂上（不是握在手裡）。

   砲管沿 local −y 延伸，與另外兩台同一個座標約定，所以 `rig.muzzleY`
   給 `render/mech-fx.ts` 掛槍口閃光不用改。

   左邊是**火箭巢**（小招的發射源）。它掛在肩上而不是手上：這台的左臂
   要短、要粗、要看得出「扛著東西」——把巢做成手持的話，剪影上兩隻手
   一樣長，大塊頭的不對稱就沒了。 */

function titanArmBase(g) {
  g.add(joint(0.34, [0, 0, 0], true));                                          // 肩關節：三台裡最粗
  var up = new THREE.Group(); up.userData.bone = 1;
  up.rotation.set(TPOSE.armPitch, TPOSE.armYaw, TPOSE.armRoll);
  g.add(up);
  up.add(bevel(MAT_P.dark, 0.48, 0.26, 0.48, [0, -0.14, 0]));                    // 上臂上段
  up.add(bevel(MAT_P.dark, 0.56, 0.32, 0.54, [0, -0.42, 0]));                    // 上臂中段（最鼓）
  up.add(bevel(MAT_P.dark, 0.42, 0.26, 0.42, [0, -0.70, 0]));                    // 上臂下段（收進肘）
  up.add(ram([0.26, -0.54, -0.20], 0.52, 0.12, 0.10, [0.12, 0, 0]));
  up.add(ram([-0.26, -0.54, -0.20], 0.52, 0.12, 0.10, [0.12, 0, 0]));
  /* 肩甲三層。兩層是 LANCER-2 的作法，這台再加一層**往外下垂的裙板** ——
     它負責把肩線拉到 x ≈ 1.75，也就是全機最寬的那個點。
     一片大板會變成正對光的方形亮牆（`lancer-arm.ts` 的實拍教訓）。 */
  up.add(plate(MAT_P.armor, 0.26, 0.64, 0.86, [-0.48, 0.04, 0], 0.12, -0.18, 0.18));
  up.add(plate(MAT_P.armor, 0.20, 0.40, 0.58, [-0.64, 0.24, 0.08], 0.12, -0.18, 0.18));
  up.add(plate(MAT_P.armor, 0.20, 0.52, 0.72, [-0.58, -0.28, 0], 0.10, -0.18, 0.44));
  up.add(rib(MAT_P.armor, 0.11, 0.11, 0.86, [-0.60, 0.34, 0], 0.12, -0.18, 0.18));
  up.add(seam(0.06, 0.60, 0.10, [-0.50, 0.06, 0.30], 0.12, -0.18, 0.30));

  var el = new THREE.Group(); el.position.set(0, -0.86, 0); up.add(el);
  el.add(joint(0.26, [0, 0, 0]));
  var fo = new THREE.Group(); fo.userData.bone = 1;
  fo.rotation.x = TPOSE.forePitch; el.add(fo);
  var aim = new THREE.Group(); aim.userData.bone = 1;
  aim.rotation.z = TPOSE.aimConverge; fo.add(aim);
  g.userData.rig = { up: up, fore: fo, aim: aim };
  return aim;
}

/** 右臂：榴彈砲。三種形狀對應三顆 MAIN 部件（`data/parts.ts` 的 MAIN_ALT）。 */
export function buildTitanCannon(shape, P?) {
  P = P || { glow: coreGlow('core-std') };
  var g = new THREE.Group();
  mark(g, shape, ['main-pierce', 'main-chain', 'main-vulcan']);
  var fo = titanArmBase(g);
  g.userData.muzzle = shape === 'main-pierce' ? -2.00 : shape === 'main-chain' ? -1.42 : -1.62;

  // 前臂：短、方、厚 —— 它是砲座不是手
  fo.add(bevel(MAT_P.dark, 0.50, 0.28, 0.50, [0, -0.16, 0]));
  fo.add(bevel(MAT_P.dark, 0.56, 0.30, 0.54, [0, -0.44, 0]));
  fo.add(plate(MAT_P.armor, 0.62, 0.66, 0.52, [0, -0.36, 0.18], 0));
  fo.add(rib(MAT_P.armor, 0.09, 0.70, 0.09, [0.26, -0.36, 0.22], 0));
  fo.add(rib(MAT_P.armor, 0.09, 0.70, 0.09, [-0.26, -0.36, 0.22], 0));
  fo.add(mesh(G.box, MAT_P.dark, [0.46, 0.36, 0.34], [0, -0.72, -0.30]));        // 砲尾配重

  if (shape === 'main-pierce') {
    /* 長管榴彈砲（`mod: heavy`）：管長、口徑小一階、砲口有制退器。
       剪影上是「伸得最遠」的那一把 —— 與它的數值（單發更重）對得上。 */
    fo.add(mesh(G.cyl, MAT_P.trim, [0.34, 0.46, 0.34], [0, -0.94, 0]));
    fo.add(mesh(G.cyl, MAT_P.trim, [0.26, 0.94, 0.26], [0, -1.48, 0]));
    fo.add(mesh(G.box, MAT_P.armor, [0.16, 0.80, 0.12], [0, -1.44, 0.22]));      // 上導軌
    fo.add(mesh(G.box, MAT_P.dark, [0.40, 0.16, 0.30], [0, -1.86, 0]));          // 制退器（橫向）
    fo.add(mesh(G.box, MAT_P.dark, [0.40, 0.16, 0.30], [0, -1.68, 0]));
    fo.add(mesh(G.box, P.glow, [0.05, 0.60, 0.05], [0, -1.30, 0.28]));

  } else if (shape === 'main-chain') {
    /* 散射榴彈砲（`mod: salvo`）：三根短管並列，一次噴一排。 */
    fo.add(mesh(G.box, MAT_P.trim, [0.62, 0.42, 0.50], [0, -0.94, 0]));
    for (var i = 0; i < 3; i++)
      fo.add(mesh(G.cyl, MAT_P.trim, [0.20, 0.56, 0.20], [(i - 1) * 0.22, -1.28, 0]));
    fo.add(mesh(G.box, MAT_P.dark, [0.66, 0.14, 0.36], [0, -1.54, 0]));
    fo.add(mesh(G.box, P.glow, [0.50, 0.06, 0.06], [0, -1.06, 0.26]));
    fo.add(rib(MAT_P.armor, 0.66, 0.10, 0.10, [0, -0.72, 0.24], 0));

  } else {  /* main-vulcan：標準榴彈砲 —— 短、粗、大口徑 */
    /* 口徑是這一把的重點：管短到只有 0.62，但直徑 0.42（另外兩台的砲管
       都在 0.16–0.20）。**粗短**在 60px 下讀得出來，長細讀不出來。 */
    fo.add(mesh(G.cyl, MAT_P.trim, [0.44, 0.34, 0.44], [0, -0.90, 0]));          // 砲膛
    fo.add(mesh(G.tor, MAT_P.dark, [0.62, 0.62, 0.20], [0, -1.02, 0], [Math.PI / 2, 0, 0]));
    fo.add(mesh(G.cyl, MAT_P.trim, [0.42, 0.62, 0.42], [0, -1.36, 0]));          // 短粗砲管
    fo.add(mesh(G.cyl, MAT_P.dark, [0.52, 0.20, 0.52], [0, -1.64, 0]));          // 砲口環
    fo.add(mesh(G.box, MAT_P.dark, [0.62, 0.12, 0.26], [0, -1.56, 0]));          // 制退口
    fo.add(mesh(G.box, P.glow, [0.06, 0.36, 0.06], [0, -1.06, 0.30]));           // 膛壓指示燈
    fo.add(mesh(G.box, MAT_P.dark, [0.34, 0.30, 0.26], [0, -0.60, -0.32]));      // 退殼口
  }
  return g;
}

/* 掛架 PYLON：左肩火箭巢。`mech-anim.ts` 的開架動畫在這裡讀成
   「巢往外掀開」—— 同一個彈簧，語意剛好對得上。 */
export function buildTitanPod(shape, P?) {
  P = P || { glow: coreGlow('core-std') };
  var g = new THREE.Group();
  mark(g, shape, ['pyl-weapon', 'pyl-passive', 'pyl-std']);
  g.add(joint(0.30, [0, 0, 0], true));
  // 左臂：短、粗，末端是抓地用的重拳（不持武器）
  g.add(bevel(MAT_P.dark, 0.46, 0.28, 0.46, [0.06, -0.16, 0]));
  g.add(bevel(MAT_P.dark, 0.52, 0.32, 0.50, [0.10, -0.46, 0]));
  g.add(bevel(MAT_P.dark, 0.44, 0.34, 0.44, [0.12, -0.80, 0]));
  g.add(mesh(G.box, MAT_P.trim, [0.44, 0.34, 0.46], [0.14, -1.08, 0.04]));       // 拳
  g.add(ram([0.34, -0.56, -0.20], 0.52, 0.11, 0.09, [0.12, 0, 0]));

  if (shape === 'pyl-weapon') {
    /* 武裝掛架：雙層八管巢，往前上方仰角 —— 火力最外顯的一種。 */
    g.add(bevel(MAT_P.dark, 0.66, 0.60, 0.72, [0.62, 0.34, 0], [-0.22, 0, -0.16]));
    for (var i = 0; i < 8; i++)
      g.add(mesh(G.cyl, MAT_P.trim, [0.15, 0.60, 0.15],
        [0.50 + (i % 2) * 0.26, 0.44 + ((i / 2) | 0) * 0.20, 0.24], [1.36, 0, -0.16]));
    g.add(rib(MAT_P.armor, 0.12, 0.12, 0.74, [0.66, 0.66, 0], 0, 0, -0.16));
    g.add(mesh(G.box, P.glow, [0.06, 0.06, 0.40], [0.80, 0.40, 0.10]));

  } else if (shape === 'pyl-passive') {
    /* 被動莢艙：巢縮小成兩管，改掛一個大彈藥箱 ——
       剪影上是「背著補給」而不是「架著火力」。 */
    g.add(bevel(MAT_P.dark, 0.62, 0.72, 0.66, [0.60, 0.24, -0.10]));
    g.add(plate(MAT_P.armor, 0.40, 0.66, 0.60, [0.78, 0.22, -0.08], 0, 0.18, -0.18));
    g.add(mesh(G.cyl, MAT_P.trim, [0.17, 0.56, 0.17], [0.52, 0.46, 0.26], [1.36, 0, -0.14]));
    g.add(mesh(G.cyl, MAT_P.trim, [0.17, 0.56, 0.17], [0.76, 0.46, 0.26], [1.36, 0, -0.14]));
    g.add(mesh(G.oct, P.glow, [0.16, 0.16, 0.16], [0.62, 0.62, -0.06]));

  } else {  /* pyl-std：標準六管火箭巢 */
    /* **巢的上緣不可以高過頭頂** —— LANCER-2 的盾第一輪就是這樣把剪影
       吃掉的。這裡巢頂在 local 0.72，而頭頂約 1.5（torso 座標）。 */
    g.add(bevel(MAT_P.dark, 0.62, 0.56, 0.68, [0.58, 0.32, 0], [-0.18, 0, -0.14]));
    for (var k = 0; k < 6; k++)
      g.add(mesh(G.cyl, MAT_P.trim, [0.16, 0.58, 0.16],
        [0.46 + (k % 3) * 0.24, 0.40 + ((k / 3) | 0) * 0.22, 0.22], [1.38, 0, -0.14]));
    g.add(plate(MAT_P.armor, 0.24, 0.52, 0.70, [0.82, 0.30, 0], 0, 0.16, -0.22));  // 外側護板
    g.add(rib(MAT_P.armor, 0.11, 0.11, 0.70, [0.62, 0.62, 0], 0, 0, -0.14));
    g.add(mesh(G.box, P.glow, [0.05, 0.05, 0.34], [0.84, 0.36, 0.08]));
    g.add(seam(0.06, 0.46, 0.10, [0.74, 0.28, 0.28], 0, 0, -0.14));
  }
  return g;
}
